import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Helmet } from 'react-helmet-async';

const { 
  FiHome, 
  FiUsers, 
  FiShoppingCart, 
  FiSettings, 
  FiCreditCard, 
  FiLogOut, 
  FiPlusCircle, 
  FiRefreshCw, 
  FiAlertCircle, 
  FiCheckCircle,
  FiMenu 
} = FiIcons;

const Dashboard = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Dashboard - Premium SMM Panel</title>
        <meta 
          name="description" 
          content="Manage your social media marketing campaigns, track orders, and monitor your growth with Premium SMM Panel's comprehensive dashboard." 
        />
      </Helmet>
      
      <div className="h-screen flex overflow-hidden">
        {/* Sidebar */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64 border-r border-gray-200 bg-white">
            <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
              <Link to="/" className="flex-1">
                <span className="text-xl font-bold text-white">
                  Premium SMM Panel
                </span>
              </Link>
            </div>
            <div className="h-0 flex-1 flex flex-col overflow-y-auto">
              <nav className="py-4 flex-1 bg-white space-y-1 px-2">
                <a href="#" className="group flex items-center px-2 py-3 text-sm font-medium rounded-md bg-gray-100 text-blue-600">
                  <SafeIcon icon={FiHome} className="mr-3 h-5 w-5 text-blue-600" />
                  Dashboard
                </a>
                <a href="#" className="group flex items-center px-2 py-3 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                  <SafeIcon icon={FiShoppingCart} className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-600" />
                  Orders
                </a>
                <a href="#" className="group flex items-center px-2 py-3 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                  <SafeIcon icon={FiCreditCard} className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-600" />
                  Payment History
                </a>
                <a href="#" className="group flex items-center px-2 py-3 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                  <SafeIcon icon={FiSettings} className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-600" />
                  Settings
                </a>
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <a href="#" className="group flex items-center px-2 py-3 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 hover:text-red-600">
                    <SafeIcon icon={FiLogOut} className="mr-3 h-5 w-5 text-gray-500 group-hover:text-red-600" />
                    Logout
                  </a>
                </div>
              </nav>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
            <button type="button" className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
              <span className="sr-only">Open sidebar</span>
              <SafeIcon icon={FiMenu} className="h-6 w-6" />
            </button>
          </div>
          
          <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none bg-gray-50">
            <div className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
              </div>
              
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
                {/* Stats */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                          <SafeIcon icon={FiShoppingCart} className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Total Orders
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              248
                            </div>
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                          <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Completed Orders
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              231
                            </div>
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                          <SafeIcon icon={FiRefreshCw} className="h-6 w-6 text-yellow-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Pending Orders
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              12
                            </div>
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                          <SafeIcon icon={FiCreditCard} className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            Balance
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              $124.00
                            </div>
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent orders */}
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-gray-900">Recent Orders</h2>
                    <a href="#" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                      View all
                    </a>
                  </div>
                  <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-200">
                    <ul className="divide-y divide-gray-200">
                      <li>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-blue-600 truncate">
                              Instagram Followers
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Completed
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 sm:flex sm:justify-between">
                            <div className="sm:flex">
                              <p className="flex items-center text-sm text-gray-500">
                                Quantity: 1000
                              </p>
                              <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                $9.99
                              </p>
                            </div>
                            <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                              <p>
                                Ordered on April 14, 2023
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      <li>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-blue-600 truncate">
                              YouTube Views
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                In Progress
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 sm:flex sm:justify-between">
                            <div className="sm:flex">
                              <p className="flex items-center text-sm text-gray-500">
                                Quantity: 5000
                              </p>
                              <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                $24.99
                              </p>
                            </div>
                            <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                              <p>
                                Ordered on April 12, 2023
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>

                      <li>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-blue-600 truncate">
                              TikTok Likes
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Completed
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 sm:flex sm:justify-between">
                            <div className="sm:flex">
                              <p className="flex items-center text-sm text-gray-500">
                                Quantity: 2500
                              </p>
                              <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                $14.99
                              </p>
                            </div>
                            <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                              <p>
                                Ordered on April 10, 2023
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;